/**
 * Timestamp formatting for Conversations.
 *
 * Times are rendered in the APPLICATION time zone, not the viewer's browser one.
 * A church runs on one clock: "Sunday 10am" means 10am in New Jersey whether you
 * read it from Seoul or from a laptop whose zone is wrong. Formatting in the
 * browser's zone would quietly show a different time to different people looking
 * at the same message.
 *
 * The zone comes from the member's stored preference, which defaults to the
 * church's own — see chat_notification_prefs.timezone.
 */

export const DEFAULT_TIMEZONE = 'America/New_York';

function safeZone(timeZone) {
  if (!timeZone) return DEFAULT_TIMEZONE;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** Local calendar day in the target zone, as YYYY-MM-DD, for grouping. */
function dayKey(date, timeZone) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatTime(value, timeZone) {
  if (!value) return '';
  return new Date(value).toLocaleTimeString('en-US', {
    timeZone: safeZone(timeZone),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * "Today" / "Yesterday" / a written date — decided in the application zone, so a
 * message sent at 11pm Eastern does not read as "Yesterday" to someone whose
 * browser is an hour ahead.
 */
export function dayLabel(value, timeZone) {
  if (!value) return '';
  const zone = safeZone(timeZone);
  const date = new Date(value);
  const now = new Date();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const key = dayKey(date, zone);
  if (key === dayKey(now, zone)) return 'Today';
  if (key === dayKey(yesterday, zone)) return 'Yesterday';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDate(value, timeZone) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(timeZone),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

/** Short relative age for the conversation list; zone-independent by nature. */
export function relativeTime(value) {
  if (!value) return '';
  const then = new Date(value);
  const seconds = Math.round((Date.now() - then.getTime()) / 1000);

  if (seconds < 60) return 'now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return formatDate(value);
}

export default { formatTime, dayLabel, formatDate, relativeTime, DEFAULT_TIMEZONE };
